"use client";

// Tiny client-side store for the user's folder list. We fetch once per
// session and let any component that mounts re-use the cache; mutations
// (create / rename / move / trash) call back into here so the sidebar
// and the projects page stay in lockstep without prop drilling.

import { useEffect, useState } from "react";
import { listFolders, type Folder } from "./api";
import { readToken } from "./auth";

const CHANGE_EVENT = "snap-site:folders-changed";

let cache: Folder[] | null = null;
let inFlight: Promise<Folder[]> | null = null;

export async function loadFolders(force = false): Promise<Folder[]> {
  if (cache && !force) return cache;
  if (inFlight && !force) return inFlight;
  const token = readToken();
  if (!token) {
    cache = [];
    return cache;
  }
  inFlight = listFolders(token)
    .then((list) => {
      cache = list;
      return list;
    })
    .catch(() => {
      cache = cache ?? [];
      return cache;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function notifyFoldersChanged(): void {
  cache = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

export function useFolders(): { folders: Folder[]; loading: boolean; refresh: () => void } {
  const [folders, setFolders] = useState<Folder[]>(cache ?? []);
  const [loading, setLoading] = useState<boolean>(cache === null);

  useEffect(() => {
    let cancelled = false;
    function refresh() {
      setLoading(true);
      loadFolders().then((list) => {
        if (cancelled) return;
        setFolders(list);
        setLoading(false);
      });
    }
    refresh();
    function onChange() {
      cache = null;
      refresh();
    }
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      cancelled = true;
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }, []);

  return {
    folders,
    loading,
    refresh: () => {
      cache = null;
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(CHANGE_EVENT));
      }
    },
  };
}
